import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Link } from '@Sage/hoi-poi-ui';

import { KpisActions, ServerListActions } from 'actions';
import PopoverHoi from 'components/PopoverHoi';
import { ACTIVITIES, EntitiesDb } from 'constants/Entities';
import Select from 'containers/components/Fields/Select';
import { findFieldConfiguration } from 'utils/fm';
import { getLiteral, getLiteralWithParametersHtml } from 'utils/getLiteral';
import { isEmpty } from 'utils/isEmpty';
import { getEmptyConditions, mapConditions } from '@Sage/ssm-web5/components/Conditions';

import ConditionsField from './ConditionsField';

import './styles.scss';

const FilterConditionByFieldMap = {
    blnischeckin: [{ field: 'idTipoGestion', valueProp: 'blnischeckin' }],
    win: [
        { field: 'idestadoempresa', valueProp: 'win' },
        { field: 'idEstadoSalesOrder', valueProp: 'win' },
    ],
    intendstatetype: [{ field: 'idestadoobra', valueProp: 'intendstatetype' }],
    blnisvisita: [{ field: 'idTipoGestion', valueProp: 'blnisvisita' }],
};

const TypeField = memo(({ changeField, data, errors, field, getError }) => {
    const dispatch = useDispatch();

    const [kpis, setKpis] = useState([]);
    const [customTypeOpen, setCustomTypeOpen] = useState(
        () => data.condition && !isEmpty(data.condition),
    );
    const [kpiTypes, setKpiTypes] = useState([]); // Used for ConditionsField
    const [editLoading, setEditLoading] = useState(() => !!data.id);
    const selectedKpiId = useRef(null);

    // Get server list options from Redux state (like ServerList component does)
    const serverListState = useSelector((state) => state.serverList);

    // ========================================
    // DERIVED STATE
    // ========================================
    const selectedKpi = useMemo(() => {
        const parentId = field.inputAttrs?.parentField;
        const parentValue = data[parentId];
        // parentValue can be object {value, label} or primitive
        const kpiIdValue = typeof parentValue === 'object' ? parentValue?.value : parentValue;
        if (!kpiIdValue) return null;
        return kpis.find((kpi) => String(kpi.value) === String(kpiIdValue));
    }, [field.inputAttrs?.parentField, data, kpis]);

    const isReadOnly = useMemo(() => {
        if (field.readOnly) return true;
        if (!selectedKpi) return false;
        return !selectedKpi.editable;
    }, [field.readOnly, selectedKpi]);

    const { list, idFieldTable } = useMemo(() => {
        if (!selectedKpi) return { list: null, idFieldTable: null };
        if (!selectedKpi.editable) return { list: null, idFieldTable: null };
        const { entity, defaultType } = selectedKpi;
        const entityConstant = EntitiesDb[entity.toLowerCase()];
        if (!entityConstant) return { list: null, idFieldTable: null };
        const { configField } = findFieldConfiguration(defaultType, entityConstant);
        return { list: configField?.valueListName, idFieldTable: configField?.id };
    }, [selectedKpi]);

    // Get options from server list (like ServerList component)
    const serverListOptions = useMemo(() => {
        if (!list) return [];
        return serverListState[list]?.data || [];
    }, [list, serverListState]);

    const error = useMemo(() => getError(errors[field.id]), [errors, field.id, getError]);

    // ========================================
    // CALLBACKS
    // ========================================
    const filterListByKpi = useCallback(
        (options) => {
            if (!options || options.length === 0) return options;
            if (!selectedKpi) return options;
            const { entity } = selectedKpi;

            let filteredOptions = [...options];

            const entityConstant = EntitiesDb[entity.toLowerCase()];
            if (entityConstant === ACTIVITIES) {
                if (filteredOptions[0]?.hasOwnProperty('blnhide')) {
                    filteredOptions = filteredOptions.filter(
                        (option) => !getBackendBoolean(option.blnhide),
                    );
                }
                if (filteredOptions[0]?.hasOwnProperty('blnisvisibleinactivity')) {
                    filteredOptions = filteredOptions.filter((option) =>
                        getBackendBoolean(option.blnisvisibleinactivity),
                    );
                }
            }

            if (selectedKpi?.filteredBy && filteredOptions?.length > 0 && idFieldTable) {
                const filterKeys = selectedKpi.filteredBy.split(',');
                filterKeys.forEach((key) => {
                    const filterBy = key.trim();
                    const filtersByConst = FilterConditionByFieldMap[filterBy.toLowerCase()];
                    if (filtersByConst) {
                        const filter = filtersByConst.find(
                            (f) => f.field.toLowerCase() === idFieldTable.toLowerCase(),
                        );
                        if (filter && filteredOptions[0]?.hasOwnProperty(filter.valueProp)) {
                            filteredOptions = filteredOptions.filter((option) =>
                                getBackendBoolean(option[filter.valueProp]),
                            );
                        }
                    }
                });
            }
            return filteredOptions;
        },
        [idFieldTable, selectedKpi],
    );

    const basicTypeChange = useCallback(
        (newValue) => {
            if (!newValue) {
                changeField(field.id)(null);
                return;
            }

            const condition = {
                entity: selectedKpi?.entity,
                field: selectedKpi?.defaultType,
                id: parseInt(newValue.value, 10),
            };

            changeField(field.id)({
                value: newValue.value,
                label: newValue.label,
                conditionSimple: condition,
            });
        },
        [changeField, field.id, selectedKpi],
    );

    const customTypeChange = useCallback(
        (conditions) => {
            changeField('condition')(conditions);
        },
        [changeField],
    );

    const toggleCustomType = useCallback(() => {
        const finalBool = !customTypeOpen;
        if (!finalBool) {
            changeField('condition')('');
        }
        changeField('activateCustomType')(finalBool);
        setCustomTypeOpen(finalBool);
    }, [changeField, customTypeOpen]);

    // ========================================
    // EFFECTS
    // ========================================
    // Load kpis list (categories)
    useEffect(() => {
        dispatch(ServerListActions.getList('kpis'))
            .then((result) => setKpis(result))
            .catch(() => console.error('Error getting kpis list'));
    }, [dispatch]);

    // Load type options from server list when list changes
    useEffect(() => {
        if (!list) return;
        dispatch(ServerListActions.getList(list));
    }, [dispatch, list]);

    // Load kpiTypes for ConditionsField
    useEffect(() => {
        if (!selectedKpi) {
            setKpiTypes([]);
            return;
        }
        if (!selectedKpi.editable) {
            setKpiTypes([]);
            return;
        }
        dispatch(KpisActions.getKpiTypes(selectedKpi))
            .then((result) => {
                setKpiTypes(result);
                if (data.id) setEditLoading(false);
            })
            .catch(console.error);
    }, [dispatch, selectedKpi, data.id]);

    useEffect(() => {
        if (data.id) return;
        if (isReadOnly && customTypeOpen) toggleCustomType();
    }, [isReadOnly, customTypeOpen, data.id, toggleCustomType]);

    useEffect(() => {
        if (
            selectedKpiId.current &&
            selectedKpiId.current !== selectedKpi?.value &&
            customTypeOpen
        ) {
            toggleCustomType();
        }
        if (selectedKpiId.current !== selectedKpi?.value) {
            selectedKpiId.current = selectedKpi?.value;
        }
    }, [selectedKpi, customTypeOpen, toggleCustomType]);

    // ========================================
    // MEMOIZED COMPONENTS
    // ========================================
    const popoverContent = useMemo(() => {
        const label = selectedKpi?.label || '';
        if (!label) return null;
        return (
            <div className="fm-type-field__popover-content">
                {getLiteralWithParametersHtml(
                    'label_category_customized',
                    [label],
                    (param, idx) => (
                        <strong key={`param-${idx}`}>{param}</strong>
                    ),
                )}
            </div>
        );
    }, [selectedKpi?.label]);

    const showInfoIcon = !data.id && isReadOnly && !customTypeOpen && popoverContent;

    // Get the current value as {value, label} object
    const value = useMemo(() => {
        const typeValue = data[field.id];
        if (!typeValue) return undefined;

        // Already has value and label
        if (typeof typeValue === 'object' && typeValue.value && typeValue.label) {
            return { value: String(typeValue.value), label: typeValue.label };
        }

        // Primitive value or object without label - need to find label from serverListOptions
        const primitiveValue = typeof typeValue === 'object' ? typeValue.value : typeValue;
        if (!primitiveValue) return undefined;

        const found = serverListOptions.find((t) => String(t.value) === String(primitiveValue));
        if (found) {
            return { value: String(found.value), label: found.label };
        }

        return { value: String(primitiveValue), label: String(primitiveValue) };
    }, [data, field.id, serverListOptions]);

    // Filter options using the kpi filter (uses serverListOptions from Redux)
    const filteredOptions = useMemo(() => {
        if (customTypeOpen) return [];
        return filterListByKpi(serverListOptions);
    }, [customTypeOpen, filterListByKpi, serverListOptions]);

    const selectProps = useMemo(
        () => ({
            error,
            hint: customTypeOpen ? getLiteral('label_custom_type') : undefined,
            isClearable: false,
            label: field.label,
            labelMode: 'horizontal',
            mandatory: field.mandatory,
            onChange: basicTypeChange,
            options: filteredOptions,
            readOnly: isReadOnly || customTypeOpen,
            value: customTypeOpen ? undefined : value,
        }),
        [
            basicTypeChange,
            customTypeOpen,
            error,
            field.label,
            field.mandatory,
            filteredOptions,
            isReadOnly,
            value,
        ],
    );

    const ConditionsComponent = useMemo(() => {
        if (!customTypeOpen || !selectedKpi?.entity) return null;
        if (data.id && editLoading) return null;

        let conditions = { ...data.condition };
        const listMapFields = kpiTypes.reduce((obj, t) => ({ ...obj, [t.value]: t.label }), {});

        if (!conditions || isEmpty(conditions)) {
            if (data.conditionSimple) {
                conditions = mapConditions({
                    '==': [
                        {
                            var: {
                                label: listMapFields[data.conditionSimple.field],
                                value: data.conditionSimple.field,
                            },
                        },
                        data.conditionSimple.id,
                    ],
                });
            } else {
                conditions = getEmptyConditions();
            }
        }

        return (
            <ConditionsField
                conditions={conditions}
                entity={selectedKpi?.entity}
                fields={kpiTypes}
                filterListByKpi={filterListByKpi}
                isReadOnly={isReadOnly}
                kpiId={selectedKpi?.id}
                listMapFields={listMapFields}
                noReMapValues={data.conditionSimple}
                onChange={customTypeChange}
            />
        );
    }, [
        customTypeChange,
        customTypeOpen,
        data.condition,
        data.conditionSimple,
        data.id,
        editLoading,
        filterListByKpi,
        isReadOnly,
        kpiTypes,
        selectedKpi,
    ]);

    if (field.hidden) return null;

    return (
        <div className="fm-type-field">
            <div className="fm-type-field__select-row">
                <Select {...selectProps} />
                {showInfoIcon && (
                    <div className="fm-type-field__info">
                        <PopoverHoi placement="top" content={popoverContent} trigger={['hover']}>
                            <span className="fm-type-field__info-icon">
                                <Icon name="info" size="small" />
                            </span>
                        </PopoverHoi>
                    </div>
                )}
            </div>
            {!data.id && !isReadOnly && (
                <div className="fm-type-field__toggle">
                    <Link type="caption" onClick={toggleCustomType}>
                        {!customTypeOpen
                            ? getLiteral('action_add_conditions')
                            : getLiteral('action_close_conditions')}
                    </Link>
                </div>
            )}
            {ConditionsComponent && (
                <div className="fm-type-field__conditions">{ConditionsComponent}</div>
            )}
        </div>
    );
});

TypeField.displayName = 'TypeField';

export default TypeField;
